import { z } from 'zod';
import { severitySchema } from './severity';
/**
 * - `dependency`       — pulled in by something else (bundled or transitive).
 * - `directDependency` — the package the MCP server itself declares.
 * - `extension`        — the IDE extension itself is the vulnerable artifact.
 */
export const OSV_COMPONENT_SUBJECTS = [
    'dependency',
    'directDependency',
    'extension',
];
/**
 * KIR-4190: the vulnerable component behind an OSV finding, rendered as its own
 * section on the alert card.
 *
 * `subject` is decided backend-side (which checker fired, and whether the
 * package sits on a dependency path); the FE must not infer it from string
 * shapes. Display-only — never part of the `finding_key`. Everything past
 * `subject`/`name`/`version` is optional: advisories routinely carry no CVSS
 * vector (`MAL-` records), no CVE alias, and no released fix.
 *
 * Lives in `shared/` because both `alerts.ts` and `analyses.ts` need it and
 * `alerts.ts` already imports `analyses.ts` — either home would cycle.
 */
export const osvComponentSchema = z.object({
    subject: z.enum(OSV_COMPONENT_SUBJECTS),
    name: z.string(),
    version: z.string(),
    /**
     * OSV ecosystem (`npm`, `PyPI`, …). `requests` on PyPI and on npm are
     * different packages. Absent for `extension`, which is not one.
     */
    ecosystem: z.string().optional(),
    /** CVE id from the advisory's `aliases`, when it has one. */
    cveId: z.string().optional(),
    /** 0–10 CVSS base score. */
    cvssScore: z.number().optional(),
    /** Band for `cvssScore`, computed backend-side so the FE never re-implements
     * the threshold table (`cvssScoreToSeverity`). */
    cvssLevel: severitySchema.optional(),
    /** Lowest released version ahead of the scanned one that fixes this advisory
     * (`affected[].ranges[].events[].fixed`). */
    fixedVersion: z.string().optional(),
    /** The advisory's one-line `summary`. */
    summary: z.string().optional(),
    /** The advisory's long-form `details`. Markdown as OSV publishes it, clipped
     * backend-side — advisories run to kilobytes and this rides on every alert's
     * `source_details`. */
    details: z.string().optional(),
});
//# sourceMappingURL=osv.js.map